import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AppearanceFields,
  type AppearanceValue,
} from "@/components/shell/BrandAppearanceFields";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import {
  AdminAlert,
  AdminPageHeader,
  AdminSectionCard,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  useAccountWorkspace,
  type WorkspaceBrand,
} from "@/lib/account-workspace";

type Membership = { user_id: number; role: "owner" | "admin" | "member" };

const brandSchema = z.object({
  name: z.string().trim().min(1, "请输入品牌名称").max(100, "品牌名称不能超过 100 个字符"),
});

type BrandFormValues = z.infer<typeof brandSchema>;

/** 品牌设置 · 基本信息：名称/图标/颜色 + 删除品牌 */
export default function BrandSettingsBasicPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accountId, brands, refreshBrands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId) ?? null;
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [appearance, setAppearance] = useState<AppearanceValue>({
    iconKey: null,
    color: null,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    defaultValues: { name: "" },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (!brand) return;
    reset({ name: brand.name });
    setAppearance({
      iconKey: brand.icon_key ?? null,
      color: brand.color ?? null,
    });
  }, [brand, reset]);

  useEffect(() => {
    if (!accountId) return;
    api
      .fetchForAccount<{ memberships: Membership[] }>(accountId, `/accounts/${accountId}/memberships`)
      .then(({ memberships }) => {
        const membership = memberships.find((item) => item.user_id === user?.id);
        setCanManage(membership?.role === "owner" || membership?.role === "admin");
      })
      .catch(() => setCanManage(false));
  }, [accountId, user?.id]);

  async function onSubmit({ name }: BrandFormValues) {
    if (!accountId || !brand) return;
    clearErrors("root");
    setError(null);
    try {
      const { brand: updated } = await api.fetchForAccount<{ brand: WorkspaceBrand }>(
        accountId,
        `/accounts/${accountId}/brands/${brand.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            icon_key: appearance.iconKey,
            color: appearance.color,
          }),
        },
      );
      reset({ name: updated.name });
      setAppearance({
        iconKey: updated.icon_key ?? null,
        color: updated.color ?? null,
      });
      await refreshBrands();
      toast.success("品牌信息已保存");
    } catch (caughtError) {
      setFormError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "保存失败，请稍后重试",
      });
    }
  }

  async function confirmDelete() {
    if (!accountId || !brand) return;
    if (deleteConfirmName.trim() !== brand.name) return;
    setDeleting(true);
    setError(null);
    try {
      await api.fetchForAccount(accountId, `/accounts/${accountId}/brands/${brand.id}`, {
        method: "DELETE",
      });
      setDeleteOpen(false);
      await refreshBrands();
      toast.success("品牌已移入回收站");
      navigate(`/accounts/${accountId}`);
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "删除失败，请稍后重试");
    } finally {
      setDeleting(false);
    }
  }

  if (!brand) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">未找到该品牌，可能已被删除。</p>
        <Link to={`/accounts/${accountId}`} className="text-sm text-primary-readable hover:underline">
          返回企业画像
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="基本信息"
        description={`更新「${brand.name}」的名称、图标与颜色。`}
      />

      {error ? <AdminAlert>{error}</AdminAlert> : null}

      <AdminSectionCard title="品牌资料" description="保存后侧栏与画像会同步更新。">
        <form className="max-w-lg space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-2">
            <Label htmlFor="brand-settings-name">品牌名称</Label>
            <Input
              id="brand-settings-name"
              {...register("name")}
              disabled={!canManage || isSubmitting}
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.name.message}
              </p>
            ) : null}
          </div>

          <AppearanceFields
            name={nameValue}
            value={appearance}
            onChange={setAppearance}
            idPrefix="brand-settings"
          />

          {errors.root ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {errors.root.message}
            </p>
          ) : null}

          {canManage ? (
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "保存中…" : "保存"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">只读授权不能修改品牌。</p>
          )}
        </form>
      </AdminSectionCard>

      {canManage ? (
        <AdminSectionCard
          title="删除品牌"
          description="品牌将进入回收站，保留 30 天，期间可恢复；到期后彻底清除。"
        >
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setDeleteConfirmName("");
              setDeleteOpen(true);
            }}
          >
            删除品牌
          </Button>
        </AdminSectionCard>
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteOpen(false);
            setDeleteConfirmName("");
          }
        }}
        title={`删除品牌「${brand.name}」？`}
        description={
          <div className="space-y-3">
            <p>品牌将进入回收站，保留 30 天。请输入品牌名称以确认。</p>
            <div className="space-y-2">
              <Label htmlFor="brand-settings-delete-confirm">
                请输入 <span className="font-medium text-foreground">{brand.name}</span>
              </Label>
              <Input
                id="brand-settings-delete-confirm"
                value={deleteConfirmName}
                onChange={(event) => setDeleteConfirmName(event.target.value)}
                placeholder={brand.name}
                autoComplete="off"
                disabled={deleting}
              />
            </div>
          </div>
        }
        confirmLabel="移入回收站"
        tone="destructive"
        confirming={deleting}
        onConfirm={() => {
          if (deleteConfirmName.trim() !== brand.name) {
            toast.error("请输入正确的品牌名称");
            return;
          }
          return confirmDelete();
        }}
      />
    </div>
  );
}
